import { supabase } from './supabase';

/*
 * Purchase posting creates a unique label per physical unit in inventory_units.
 * A few older screens search only product_variants.barcode / sku in their local
 * catalogue, so a perfectly valid freshly-printed unit label looks like "no
 * match" there. The database already has the authoritative resolver used by POS
 * and transfer verification. This bridge only helps the LOCAL search boxes: it
 * turns a unit/batch label into that variant's ordinary catalogue code and then
 * lets the screen's existing Enter handler do the add. It does not touch stock,
 * quantity or accounting.
 */
const LOCAL_SCAN_PLACEHOLDERS = [
  'scan barcode or search exact product / variant',
  'scan barcode or search product / variant',
];

function isLocalScanSearch(input) {
  if (!(input instanceof HTMLInputElement)) return false;
  const placeholder = String(input.placeholder || '').trim().toLowerCase();
  return LOCAL_SCAN_PLACEHOLDERS.some(text => placeholder.includes(text));
}

function setReactInputValue(input, value) {
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
  if (setter) setter.call(input, value);
  else input.value = value;
  input.dispatchEvent(new Event('input', { bubbles: true }));
}

async function canonicalCode(scanned) {
  const code = String(scanned || '').trim();
  if (!code) return null;
  const { data, error } = await supabase.rpc('resolve_barcode', { p_code: code });
  if (error || !Array.isArray(data) || !data[0]?.variant_id) return null;
  /* Normal variant barcodes already work locally; do not intercept them. */
  if (String(data[0].matched_via || '').toLowerCase() === 'variant') return null;
  const { data: variant, error: variantError } = await supabase
    .from('product_variants')
    .select('barcode,sku')
    .eq('id', data[0].variant_id)
    .maybeSingle();
  if (variantError || !variant) return null;
  return String(variant.barcode || variant.sku || '').trim() || null;
}

async function handleEnter(event) {
  if (event.key !== 'Enter' || event.isComposing) return;
  const input = event.target;
  if (!isLocalScanSearch(input)) return;
  /* The synthetic Enter after canonicalisation must reach the screen itself. */
  if (input.dataset.hbCanonicalScan === '1') {
    delete input.dataset.hbCanonicalScan;
    return;
  }
  const raw = String(input.value || '').trim();
  if (!raw) return;

  const canonical = await canonicalCode(raw);
  if (!canonical || canonical.toLowerCase() === raw.toLowerCase()) return;

  event.preventDefault();
  event.stopPropagation();
  setReactInputValue(input, canonical);
  input.dataset.hbCanonicalScan = '1';
  window.setTimeout(() => {
    input.dispatchEvent(new KeyboardEvent('keydown', {
      key: 'Enter', code: 'Enter', bubbles: true, cancelable: true,
    }));
  }, 40);
}

/* Capture is intentional: React delegates keydown at the app root. We only
 * stop the original event when a unit/batch code was actually translated. */
document.addEventListener('keydown', event => { void handleEnter(event); }, true);
